import dayjs from 'dayjs'
import { useLocalStorage } from '@dhl/use-storage'

type Responded = {
  date: string
  type: 'responded'
}

type Dismissed = {
  date: string
  type: 'dismissed'
}

type State = Responded | Dismissed

type SurveyStateRecord = Record<number, State>

type Return = {
  getSurveyState: (surveyId: number) => State | undefined
  shouldShowSurvey: (surveyId: number) => boolean
  dismissSurvey: (surveyId: number) => void
  markSurveyAsResponded: (surveyId: number) => void
}

export const useMopinionState = (): Return => {
  const [state, setState] = useLocalStorage<SurveyStateRecord>('mopinionState', {})

  const getSurveyState = (surveyId: number): State | undefined => state[surveyId]

  const setSurveyState = (surveyId: number, surveyState: State) => {
    setState({
      ...state,
      [surveyId]: surveyState,
    })
  }

  const dismissSurvey = (surveyId: number): void => {
    setSurveyState(surveyId, {
      date: new Date().toISOString(),
      type: 'dismissed',
    })
  }

  const markSurveyAsResponded = (surveyId: number): void => {
    setSurveyState(surveyId, {
      type: 'responded',
      date: new Date().toISOString(),
    })
  }

  const shouldShowSurvey = (surveyId: number): boolean => {
    const surveyState = getSurveyState(surveyId)

    if (surveyState?.type === 'dismissed' || surveyState?.type === 'responded') {
      const lastResponseDate = dayjs(surveyState.date)
      const monthsSinceLastResponse = dayjs().diff(lastResponseDate, 'month')
      return monthsSinceLastResponse >= 6 // Show survey if last response was more than 6 months ago
    }

    return true
  }

  return { getSurveyState, shouldShowSurvey, dismissSurvey, markSurveyAsResponded }
}
